import { isFulfilled, isRejected } from "@reduxjs/toolkit";
import { useEffect } from "react";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import SendUserMessageDialog from "../../../common/components/functional/sendUserMessage/SendUserMessageDialog";
import {
  chunkArray,
  toUserMessageChannels,
} from "../../userMessages/userMessagesHelpers";
import {
  addUserMessageRecipients,
  createUserMessage,
  fetchAttachmentUploadUrl,
  resetSendStatus,
  startUserMessageProcessing,
  uploadUserMessageAttachment,
} from "../../userMessages/userMessagesSlice";
import { Talent } from "../talentAPI";

const BATCH_SIZE = 500;

interface Props {
  open: boolean;
  onClose: () => void;
  customerOrgId: string | undefined;
  selectedTalentIds: string[];
  allTalents: Talent[];
  onRemoveRecipient: (talentId: string) => void;
}

const SendTalentsUserMessageDialog = ({
  open,
  onClose,
  customerOrgId,
  selectedTalentIds,
  allTalents,
  onRemoveRecipient,
}: Props) => {
  const dispatch = useAppDispatch();
  const sendStatus = useAppSelector((state) => state.userMessages.sendStatus);
  const isSending = sendStatus === "pending";

  useEffect(() => {
    if (open) {
      dispatch(resetSendStatus());
    }
  }, [open, dispatch]);

  const recipients = selectedTalentIds
    .map((id) => allTalents.find((t) => t.id === id))
    .filter((talent): talent is Talent => !!talent)
    .map((talent) => ({
      id: talent.id,
      name: `${talent.account.firstName} ${talent.account.lastName}`,
      email: talent.account.emailAddress,
    }));

  const handleSend = async ({
    subject,
    messageHtml,
    fromNoReplyAddress,
    attachments,
    sendAsEmail,
    sendAsInApp,
  }: {
    subject: string;
    messageHtml: string;
    fromNoReplyAddress: boolean;
    attachments: File[];
    sendAsEmail: boolean;
    sendAsInApp: boolean;
  }) => {
    if (!customerOrgId || selectedTalentIds.length === 0) return;

    const channels = toUserMessageChannels(sendAsEmail, sendAsInApp);
    if (channels.length === 0) return;

    const firstBatch = selectedTalentIds.slice(0, BATCH_SIZE);
    const remainingBatches = chunkArray(
      selectedTalentIds.slice(BATCH_SIZE),
      BATCH_SIZE,
    );

    const createResult = await dispatch(
      createUserMessage({
        subject,
        messageHtml,
        fromNoReplyAddress,
        context: { type: "TalentCommonMessage", customerOrgId },
        recipientUserIds: firstBatch,
        channels,
      }),
    );

    if (isRejected(createResult)) return;

    const messageId = isFulfilled(createResult)
      ? createResult.payload.find((e) => e.name === "idn-usrMsg-created")?.id
      : undefined;

    if (!messageId) {
      dispatch(resetSendStatus());
      return;
    }

    for (const batch of remainingBatches) {
      const addResult = await dispatch(
        addUserMessageRecipients({ messageId, recipientUserIds: batch }),
      );
      if (isRejected(addResult)) return;
    }

    for (const file of attachments) {
      const uploadUrlResult = await dispatch(
        fetchAttachmentUploadUrl({ messageId, filename: file.name, size: file.size }),
      );

      if (!isFulfilled(uploadUrlResult)) return;

      const uploadResult = await dispatch(
        uploadUserMessageAttachment({ url: uploadUrlResult.payload.url, file }),
      );

      if (isRejected(uploadResult)) return;
    }

    const startResult = await dispatch(
      startUserMessageProcessing({ messageId }),
    );

    if (isFulfilled(startResult)) {
      onClose();
    }
  };

  return (
    <SendUserMessageDialog
      open={open}
      onClose={onClose}
      senderMode={{ type: "selectable" }}
      recipients={recipients}
      isSending={isSending}
      onRemoveRecipient={onRemoveRecipient}
      onSend={handleSend}
    />
  );
};

export default SendTalentsUserMessageDialog;
